import { create } from 'zustand';
import { WINDOW_CONFIGS } from '@/lib/config/windows';

export type WindowStatus = 'open' | 'minimized' | 'closed' | 'maximized';

export type ManagedWindow = {
	id: string;
	title: string;
	icon: string;
	status: WindowStatus;
	zIndex: number;
	width: number;
	height?: number;
	minWidth?: number;
	minHeight?: number;
	isShaking?: boolean;
};

type WindowStore = {
	windows: ManagedWindow[];
	openWindow: (id: string | string[]) => void;
	closeWindow: (id: string) => void;
	closeAllWindow: () => void;
	minimizeWindow: (id: string) => void;
	toggleMaximize: (id: string) => void;
	bringToFront: (id: string) => void;
	resizeWindow: (id: string, width: number, height?: number) => void;
	shakeWindow: (id: string) => void;
};

const topZ = (windows: ManagedWindow[]) =>
	windows.reduce((m, w) => Math.max(m, w.zIndex), 0);

const INITIAL_WINDOWS: ManagedWindow[] = WINDOW_CONFIGS.map(
	({ element: _el, contentClassName: _cc, initialPosition: _ip, ...rest }) =>
		rest,
);

export const useWindowStore = create<WindowStore>((set) => ({
	windows: INITIAL_WINDOWS,

	openWindow: (id) =>
		set(({ windows }) => {
			if (Array.isArray(id)) {
				const idSet = new Set(id);
				return {
					windows: windows.map((w) =>
						idSet.has(w.id)
							? { ...w, status: 'open' as const, zIndex: topZ(windows) + 1 }
							: w,
					),
				};
			}

			const visibleWindows = windows.filter(
				(w) => w.status === 'open' || w.status === 'maximized',
			);
			const maxZ =
				visibleWindows.length > 0
					? Math.max(...visibleWindows.map((w) => w.zIndex))
					: 0;

			return {
				windows: windows.map((w) => {
					if (w.id !== id) return w;

					if (w.status === 'closed' || w.status === 'minimized') {
						return { ...w, status: 'open' as const, zIndex: topZ(windows) + 1 };
					}

					if (w.zIndex === maxZ) {
						return { ...w, isShaking: true };
					}

					return { ...w, zIndex: topZ(windows) + 1 };
				}),
			};
		}),

	closeWindow: (id) =>
		set(({ windows }) => ({
			windows: windows.map((w) =>
				w.id === id ? { ...w, status: 'closed' } : w,
			),
		})),
	closeAllWindow: () =>
		set(({ windows }) => ({
			windows: windows.map((w) => ({ ...w, status: 'closed' })),
		})),

	minimizeWindow: (id) =>
		set(({ windows }) => ({
			windows: windows.map((w) =>
				w.id === id ? { ...w, status: 'minimized' } : w,
			),
		})),

	toggleMaximize: (id) =>
		set(({ windows }) => ({
			windows: windows.map((w) =>
				w.id === id
					? {
							...w,
							status: w.status === 'maximized' ? 'open' : 'maximized',
							zIndex: topZ(windows) + 1,
						}
					: w,
			),
		})),

	bringToFront: (id) =>
		set(({ windows }) => ({
			windows: windows.map((w) =>
				w.id === id ? { ...w, zIndex: topZ(windows) + 1 } : w,
			),
		})),

	resizeWindow: (id, width, height) =>
		set(({ windows }) => ({
			windows: windows.map((w) => (w.id === id ? { ...w, width, height } : w)),
		})),

	shakeWindow: (id) =>
		set(({ windows }) => ({
			windows: windows.map((w) =>
				w.id === id ? { ...w, isShaking: true } : w,
			),
		})),
}));

export function useWindowControl(id: string) {
	const openWindow = useWindowStore((s) => s.openWindow);
	const closeWindow = useWindowStore((s) => s.closeWindow);
	const minimizeWindow = useWindowStore((s) => s.minimizeWindow);
	const toggleMaximize = useWindowStore((s) => s.toggleMaximize);
	const bringToFront = useWindowStore((s) => s.bringToFront);
	const resizeWindow = useWindowStore((s) => s.resizeWindow);
	const shakeWindow = useWindowStore((s) => s.shakeWindow);
	const win = useWindowStore((s) => s.windows.find((w) => w.id === id));
	return {
		win,
		open: () => openWindow(id),
		close: () => closeWindow(id),
		minimize: () => minimizeWindow(id),
		toggleMaximize: () => toggleMaximize(id),
		bringToFront: () => bringToFront(id),
		resize: (width: number, height?: number) => resizeWindow(id, width, height),
		shake: () => shakeWindow(id),
		clearShake: () =>
			useWindowStore.setState((s) => ({
				windows: s.windows.map((w) =>
					w.id === id ? { ...w, isShaking: false } : w,
				),
			})),
	};
}

/** True when this window has the highest zIndex among visible windows */
export function useIsFocused(id: string) {
	return useWindowStore((s) => {
		const visible = s.windows.filter(
			(w) => w.status === 'open' || w.status === 'maximized',
		);
		if (!visible.length) return false;
		const maxZ = Math.max(...visible.map((w) => w.zIndex));
		return (s.windows.find((w) => w.id === id)?.zIndex ?? 0) === maxZ;
	});
}
